import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getMeetingsAnalyticsSummary, getMeetingsAnalyticsByAccount } from '../api';
import { useTheme } from '../context/ThemeContext';

const DETAIL_POPOVER_WIDTH = 340;
// Controls how much content is visible before the internal list scrolls.
const DETAIL_POPOVER_MAX_HEIGHT = 560;

const MAX_RANGE_DAYS = 366;

function toDateOnly(d) {
  const x = new Date(d);
  return x.toISOString().slice(0, 10);
}

function nDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateOnly(d);
}

function startOfMonth() {
  const d = new Date();
  return toDateOnly(new Date(d.getFullYear(), d.getMonth(), 1));
}

const DATE_PRESETS = [
  { id: 'last_7', label: 'Last 7 days' },
  { id: 'last_30', label: 'Last 30 days' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_90', label: 'Last 90 days' },
];

function getRangeForPreset(presetId) {
  const todayStr = toDateOnly(new Date());
  switch (presetId) {
    case 'last_7':
      return { start: nDaysAgo(6), end: todayStr };
    case 'last_30':
      return { start: nDaysAgo(29), end: todayStr };
    case 'this_month':
      return { start: startOfMonth(), end: todayStr };
    case 'last_90':
      return { start: nDaysAgo(89), end: todayStr };
    default:
      return null;
  }
}

const SUMMARY_METRICS = [
  { key: 'totalCount', label: 'Total Meeting Events', fromStatus: false, color: '#2563eb', bgKey: 'primarySoft' },
  { key: 'transcriptEnabled', label: 'Transcript enabled', fromStatus: true, color: '#0d9488', bgKey: 'tealSoft' },
  { key: 'waitingTimeout', label: 'Bot not allowed to join', fromStatus: true, color: '#d97706', bgKey: 'amberSoft' },
  { key: 'deniedJoining', label: 'Bot denied to join', fromStatus: true, color: '#dc2626', bgKey: 'roseSoft' },
  { key: 'transcriptProcessed', label: 'Transcript processed', fromStatus: true, color: '#7c3aed', bgKey: 'violetSoft' },
];

/** Display order + labels for statusCounts (tooltip and unknown keys appended after). */
const STATUS_COUNT_KEY_ORDER = [
  'transcriptNotEnabled',
  'joining',
  'transcriptEnabled',
  'kickedFromCall',
  'waitingTimeout',
  'recordingTimeout',
  'meetingEnded',
  'everyoneLeft',
  'voiceInactivity',
  'deniedJoining',
  'missingLink',
  'exclusionMeeting',
  'pastEvent',
  'transcriptProcessed',
];

const STATUS_COUNT_LABELS = {
  transcriptNotEnabled: 'Transcript not enabled',
  joining: 'Joining',
  transcriptEnabled: 'Transcript enabled',
  kickedFromCall: 'Kicked from call',
  waitingTimeout: 'Waiting timeout',
  recordingTimeout: 'Recording timeout',
  meetingEnded: 'Meeting ended',
  everyoneLeft: 'Everyone left',
  voiceInactivity: 'Voice inactivity',
  deniedJoining: 'Bot denied to join',
  missingLink: 'Missing link',
  exclusionMeeting: 'Exclusion meeting',
  pastEvent: 'Past event',
  transcriptProcessed: 'Transcript processed',
};

function buildWorkspaceDetailItems(row) {
  const items = [];
  items.push({
    label: 'Total Meeting Events',
    key: 'totalCount',
    value: row.totalCount ?? 0,
    emphasis: true,
  });
  const sc = row.statusCounts && typeof row.statusCounts === 'object' ? row.statusCounts : {};
  const seen = new Set();
  for (const key of STATUS_COUNT_KEY_ORDER) {
    if (!Object.prototype.hasOwnProperty.call(sc, key)) continue;
    seen.add(key);
    items.push({
      label: STATUS_COUNT_LABELS[key] || key,
      key,
      value: sc[key] ?? 0,
      emphasis: false,
    });
  }
  for (const key of Object.keys(sc)) {
    if (seen.has(key)) continue;
    items.push({
      label: STATUS_COUNT_LABELS[key] || key,
      key,
      value: sc[key] ?? 0,
      emphasis: false,
    });
  }
  return items;
}

function computePopoverPosition(triggerRect) {
  const pad = 12;
  let left = triggerRect.right - DETAIL_POPOVER_WIDTH;
  if (left < pad) left = pad;
  if (left + DETAIL_POPOVER_WIDTH > window.innerWidth - pad) {
    left = window.innerWidth - DETAIL_POPOVER_WIDTH - pad;
  }
  let top = triggerRect.bottom + 8;
  if (top + DETAIL_POPOVER_MAX_HEIGHT > window.innerHeight - pad) {
    top = Math.max(pad, triggerRect.top - DETAIL_POPOVER_MAX_HEIGHT - 8);
  }
  return { left, top };
}

export default function MeetingsAnalytics() {
  const { theme, isDarkMode } = useTheme();
  const [selectedPreset, setSelectedPreset] = useState('last_30');
  const [environment, setEnvironment] = useState('production');
  const [start, setStart] = useState(() => getRangeForPreset('last_30').start);
  const [end, setEnd] = useState(() => getRangeForPreset('last_30').end);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailTip, setDetailTip] = useState(null);
  const detailLeaveTimer = useRef(null);

  const clearDetailLeaveTimer = () => {
    if (detailLeaveTimer.current) {
      clearTimeout(detailLeaveTimer.current);
      detailLeaveTimer.current = null;
    }
  };

  const scheduleCloseDetailTip = () => {
    clearDetailLeaveTimer();
    detailLeaveTimer.current = setTimeout(() => {
      setDetailTip(null);
      detailLeaveTimer.current = null;
    }, 120);
  };

  useEffect(() => () => clearDetailLeaveTimer(), []);

  const softBg = {
    primarySoft: isDarkMode ? 'rgba(37, 99, 235, 0.15)' : '#eff6ff',
    tealSoft: isDarkMode ? 'rgba(13, 148, 136, 0.15)' : '#f0fdfa',
    violetSoft: isDarkMode ? 'rgba(124, 58, 237, 0.15)' : '#f5f3ff',
    amberSoft: isDarkMode ? 'rgba(217, 119, 6, 0.15)' : '#fffbeb',
    roseSoft: isDarkMode ? 'rgba(220, 38, 38, 0.15)' : '#fff1f2',
  };

  const rangeParams = useCallback(() => {
    const startDate = new Date(`${start}T00:00:00`);
    const endDate = new Date(`${end}T23:59:59.999`);
    return {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      environment,
    };
  }, [start, end, environment]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = rangeParams();
      const [summaryRes, accountsRes] = await Promise.all([
        getMeetingsAnalyticsSummary(params),
        getMeetingsAnalyticsByAccount(params),
      ]);
      setSummary(summaryRes.data);
      const list = Array.isArray(accountsRes.data) ? accountsRes.data : [];
      setRows([...list].sort((a, b) => (b.totalCount || 0) - (a.totalCount || 0)));
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to load meetings analytics';
      setError(msg);
      setSummary(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [rangeParams]);

  useEffect(() => {
    fetchData();
  }, []);

  const setPresetAndRange = (presetId) => {
    setSelectedPreset(presetId);
    const r = getRangeForPreset(presetId);
    if (r) {
      setStart(r.start);
      setEnd(r.end);
    }
  };

  const handleStartChange = (value) => {
    setSelectedPreset(null);
    setStart(value);
  };

  const handleEndChange = (value) => {
    setSelectedPreset(null);
    setEnd(value);
  };

  const getMetricValue = (metric) => {
    if (!summary) return null;
    if (metric.fromStatus) {
      return summary.statusCounts?.[metric.key] ?? 0;
    }
    return summary.totalCount ?? 0;
  };

  const inputStyle = {
    padding: '10px 12px',
    borderRadius: '8px',
    border: `1px solid ${theme.inputBorder}`,
    backgroundColor: theme.inputBackground,
    color: theme.text,
    fontSize: '14px',
    minWidth: '160px',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: theme.textSecondary,
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  };

  return (
    <>
    <div style={{ padding: '24px 28px 48px'}}>
      <h1 style={{ margin: '0 0 8px', fontSize: '26px', fontWeight: 700, color: theme.text }}>
        Meetings analytics
      </h1>
      <p style={{ margin: '0 0 24px', fontSize: '14px', color: theme.textSecondary, lineHeight: 1.5 }}>
        Summary and per-workspace meeting events from Profile Analyser for the selected window.
      </p>

      <div
        style={{
          backgroundColor: theme.cardBackground,
          borderRadius: '14px',
          border: `1px solid ${theme.border}`,
          boxShadow: `0 2px 12px ${theme.cardShadow}`,
          padding: '20px 22px',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '16px 20px' }}>
          <div>
            <label style={labelStyle}>Environment</label>
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer', minWidth: '140px' }}
            >
              <option value="production">Production</option>
              <option value="staging">Staging</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Quick range</label>
            <select
              value={selectedPreset ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  setSelectedPreset(null);
                  return;
                }
                setPresetAndRange(v);
              }}
              style={{ ...inputStyle, cursor: 'pointer', minWidth: '180px' }}
            >
              <option value="">Custom range</option>
              {DATE_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Meeting start time</label>
            <input type="date" value={start} onChange={(e) => handleStartChange(e.target.value)} style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Meeting end time</label>
            <input type="date" value={end} onChange={(e) => handleEndChange(e.target.value)} style={inputStyle} />
          </div>

          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            style={{
              padding: '10px 22px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: loading ? theme.textMuted : theme.primary,
              color: '#fff',
              fontWeight: 600,
              fontSize: '14px',
              cursor: loading ? 'not-allowed' : 'pointer',
              alignSelf: 'flex-end',
            }}
          >
            {loading ? 'Loading…' : 'Apply'}
          </button>
        </div>

        <p style={{ margin: '14px 0 0', fontSize: '12px', color: theme.textMuted }}>
          Times use the start of the start day and end of the end day in your local timezone.
        </p>
      </div>

      {error && (
        <div
          style={{
            padding: '14px 16px',
            borderRadius: '10px',
            backgroundColor: isDarkMode ? 'rgba(231, 76, 60, 0.15)' : '#fdecea',
            color: theme.danger,
            marginBottom: '20px',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '28px',
        }}
      >
        {SUMMARY_METRICS.map((metric) => {
          const value = getMetricValue(metric);
          const bg = softBg[metric.bgKey] || theme.backgroundSecondary;
          return (
            <div
              key={metric.key}
              style={{
                padding: '20px 18px',
                borderRadius: '14px',
                backgroundColor: bg,
                border: `1px solid ${metric.color}33`,
                boxShadow: `0 2px 8px ${theme.cardShadow}`,
              }}
            >
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: metric.color,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  marginBottom: '10px',
                }}
              >
                {metric.label}
              </div>
              <div style={{ fontSize: '30px', fontWeight: 700, color: theme.text, lineHeight: 1.1 }}>
                {loading ? '—' : (value ?? 0).toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          backgroundColor: theme.cardBackground,
          borderRadius: '14px',
          border: `1px solid ${theme.border}`,
          boxShadow: `0 2px 12px ${theme.cardShadow}`,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: `1px solid ${theme.border}`,
            fontSize: '16px',
            fontWeight: 600,
            color: theme.text,
          }}
        >
          By workspace
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: theme.tableHeader }}>
                <th style={{ textAlign: 'left', padding: '12px 16px', color: theme.textSecondary, fontWeight: 600 }}>
                  Slug
                </th>
                <th style={{ textAlign: 'right', padding: '12px 16px', color: theme.textSecondary, fontWeight: 600 }}>
                  Total count
                </th>
                <th style={{ textAlign: 'right', padding: '12px 16px', color: theme.textSecondary, fontWeight: 600 }}>
                  Transcript enabled
                </th>
                <th style={{ textAlign: 'right', padding: '12px 16px', color: theme.textSecondary, fontWeight: 600 }}>
                  Bot not allowed to join
                </th>
                <th style={{ textAlign: 'right', padding: '12px 16px', color: theme.textSecondary, fontWeight: 600 }}>
                  Transcript processed
                </th>
                <th style={{ textAlign: 'center', padding: '12px 12px', color: theme.textSecondary, fontWeight: 600, width: 56 }}>
                  Details
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '28px 16px', textAlign: 'center', color: theme.textMuted }}>
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '28px 16px', textAlign: 'center', color: theme.textMuted }}>
                    No rows for this range.
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => {
                  const rowKey = String(row.accountId || row.slug || i);
                  return (
                  <tr
                    key={rowKey}
                    style={{
                      backgroundColor: i % 2 === 0 ? 'transparent' : theme.tableRowAlternate,
                      borderTop: `1px solid ${theme.borderLight}`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.tableRowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = i % 2 === 0 ? 'transparent' : theme.tableRowAlternate;
                    }}
                  >
                    <td style={{ padding: '12px 16px', color: theme.text, fontWeight: 500 }}>{row.slug || '—'}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: theme.text, fontVariantNumeric: 'tabular-nums' }}>
                      {(row.totalCount ?? 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: theme.text, fontVariantNumeric: 'tabular-nums' }}>
                      {(row.statusCounts?.transcriptEnabled ?? 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: theme.text, fontVariantNumeric: 'tabular-nums' }}>
                      {(row.statusCounts?.deniedJoining ?? 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: theme.text, fontVariantNumeric: 'tabular-nums' }}>
                      {(row.statusCounts?.transcriptProcessed ?? 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        padding: '12px 8px',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        position: 'relative',
                      }}
                    >
                      <span
                        role="button"
                        aria-label="All meeting status counts for this workspace"
                        tabIndex={0}
                        onMouseEnter={(e) => {
                          clearDetailLeaveTimer();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const pos = computePopoverPosition(rect);
                          setDetailTip({
                            slug: row.slug,
                            items: buildWorkspaceDetailItems(row),
                            ...pos,
                          });
                        }}
                        onMouseLeave={scheduleCloseDetailTip}
                        onFocus={(e) => {
                          clearDetailLeaveTimer();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const pos = computePopoverPosition(rect);
                          setDetailTip({
                            slug: row.slug,
                            items: buildWorkspaceDetailItems(row),
                            ...pos,
                          });
                        }}
                        onBlur={scheduleCloseDetailTip}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          cursor: 'pointer',
                          color: '#fff',
                          backgroundColor: theme.primary,
                          border: 'none',
                          fontSize: '14px',
                          fontWeight: 700,
                          fontStyle: 'italic',
                          fontFamily: 'Georgia, serif',
                          lineHeight: 1,
                          userSelect: 'none',
                          boxShadow: `0 2px 6px ${theme.cardShadow}`,
                        }}
                      >
                        i
                      </span>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>

    {detailTip &&
      createPortal(
        <div
          role="dialog"
          aria-label="Meeting status breakdown"
          onMouseEnter={clearDetailLeaveTimer}
          onMouseLeave={scheduleCloseDetailTip}
          style={{
            position: 'fixed',
            left: detailTip.left,
            top: detailTip.top,
            width: DETAIL_POPOVER_WIDTH,
            maxHeight: DETAIL_POPOVER_MAX_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 10050,
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: theme.cardBackground,
            border: `1px solid ${theme.border}`,
            boxShadow: isDarkMode
              ? '0 16px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)'
              : '0 16px 48px rgba(15, 23, 42, 0.14), 0 4px 12px rgba(15, 23, 42, 0.08)',
          }}
        >
          <div
            style={{
              flexShrink: 0,
              padding: '12px 16px',
              borderBottom: `1px solid ${theme.border}`,
              background: isDarkMode
                ? `linear-gradient(180deg, ${theme.tableHeader} 0%, ${theme.cardBackground} 100%)`
                : theme.tableHeader,
            }}
          >
            <div
              style={{
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.1em',
                color: theme.textMuted,
                textTransform: 'uppercase',
              }}
            >
              Workspace metrics
            </div>
            <div
              style={{
                fontSize: '15px',
                fontWeight: 700,
                color: theme.text,
                marginTop: '6px',
                wordBreak: 'break-word',
                lineHeight: 1.3,
              }}
            >
              {detailTip.slug || '—'}
            </div>
          </div>
          <div
            style={{
              overflowY: 'auto',
              flex: 1,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {detailTip.items.map((item, idx) => (
              <div
                key={`${item.key}-${idx}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  gap: '12px',
                  alignItems: 'center',
                  padding: '11px 16px',
                  borderBottom:
                    idx < detailTip.items.length - 1 ? `1px solid ${theme.borderLight}` : 'none',
                  backgroundColor: item.emphasis
                    ? isDarkMode
                      ? 'rgba(77, 171, 247, 0.1)'
                      : 'rgba(0, 123, 255, 0.07)'
                    : 'transparent',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: item.emphasis ? 600 : 500,
                      color: theme.text,
                      lineHeight: 1.35,
                    }}
                  >
                    {item.label}
                  </div>
                </div>
                <div
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 700,
                    fontSize: '16px',
                    color: theme.primary,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {Number(item.value).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
